import { Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useOnScreen } from "../../../domains/app/hooks/useOnScreen";
import { IconMessageCircle, IconSquareRoundedPlus } from "@tabler/icons-react";
import ProfilePicture from "../../ProfilePicture";
import Skeleton from "../../skeleton/Skeleton";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useApiInfiniteQuery, useApiRevalidate } from "../../../services/api";
import { messageText } from "../../../domains/gpt/transcript";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/Button";
import { GPT_HEADER_ROW } from "../layout";

const RECENTS_SKELETON_WIDTHS = [180, 140, 200, 120, 160] as const;

const EVIDENCE_PATH = "/webapp/private/evidence-gpt/conversations";
const GENERAL_PATH = "/webapp/private/conversations";

type Recent = { id: string; label: string; sortDate: string };

/**
 * The push-aside history drawer for the Evidence GPT surface. It lists the
 * user's FULL conversation history — both Evidence threads (`EVIDENCE_PATH`)
 * and general JuisciGPT threads (`GENERAL_PATH`, which deliberately excludes
 * evidence). A flagged user must not lose access to their existing
 * conversations; opening any row lands on the shared conversation route, and
 * the origin dispatcher renders the right agent (general vs Evidence) for that
 * thread. Both feeds are paginated (infinite) and driven to exhaustion by the
 * bottom sentinel, so no thread from either feed is ever capped out of reach.
 * Fetches only while open.
 */
export function EvidenceHistoryDrawer({
  isOpen,
  roomId,
  onNewConversation,
}: {
  isOpen: boolean;
  roomId: string | null;
  onNewConversation: () => void;
}) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const { gptConversationPageLink } = usePageLinks(roomId);
  const revalidate = useApiRevalidate();

  const evidenceQuery = useApiInfiniteQuery(
    EVIDENCE_PATH,
    isOpen ? { query: { limit: 20 } } : false
  );
  const generalQuery = useApiInfiniteQuery(
    GENERAL_PATH,
    isOpen ? { query: { limit: 30, sort: "newest" } } : false
  );

  const anyPending =
    isOpen &&
    (evidenceQuery.state === "pending" || generalQuery.state === "pending");
  // A transient failure of EITHER feed must not read as "no history" — surface
  // it explicitly with a retry instead of silently dropping that feed's threads.
  const hasError =
    evidenceQuery.state === "error" || generalQuery.state === "error";
  const bothSucceeded =
    evidenceQuery.state === "success" && generalQuery.state === "success";

  // Advance BOTH feeds when the end-of-list sentinel is on screen (fires on
  // scroll AND on mount when the list doesn't overflow), so the merged history
  // pages through to exhaustion rather than capping either feed.
  const { ref: loadMoreRef, isIntersecting } = useOnScreen();
  useEffect(() => {
    if (!isIntersecting) return;
    if (evidenceQuery.state === "success" && evidenceQuery.canFetchNextPage)
      evidenceQuery.fetchNextPage();
    if (generalQuery.state === "success" && generalQuery.canFetchNextPage)
      generalQuery.fetchNextPage();
  }, [isIntersecting, evidenceQuery, generalQuery]);

  const recents = useMemo((): Recent[] => {
    const items: Recent[] = [];
    if (evidenceQuery.state === "success") {
      for (const doc of evidenceQuery.docs) {
        const label =
          doc.title !== null
            ? doc.title
            : doc.firstQuestion !== null
              ? doc.firstQuestion
              : t("Untitled thread");
        items.push({ id: doc.id, label, sortDate: doc.createdAt });
      }
    }
    if (generalQuery.state === "success") {
      for (const doc of generalQuery.docs) {
        // A conversation is titled asynchronously after its first turn; until
        // then the opening question is the only label it has.
        const firstMessage = doc.messages.at(0);
        const label =
          doc.title !== null
            ? doc.title
            : firstMessage === undefined
              ? t("Untitled thread")
              : messageText(firstMessage);
        items.push({ id: doc.id, label, sortDate: doc.createdAt });
      }
    }
    return items.sort(
      (a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
    );
  }, [evidenceQuery, generalQuery, t]);

  // Refetch both feeds. No input → invalidate by the path-level key, which
  // matches every page of each infinite query regardless of params.
  function retry() {
    void revalidate(EVIDENCE_PATH);
    void revalidate(GENERAL_PATH);
  }

  return (
    <>
      <div
        className={`fixed inset-0 z-1 bg-ds-cream transition-opacity duration-300 ease-in-out ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <div
        className={`fixed top-0 left-0 z-1 flex h-dvh w-4/5 max-w-96 flex-col transition-transform duration-300 ease-in-out will-change-transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ paddingTop: "calc(var(--safe-area-top) + 1.5rem)" }}
      >
        {currentUser !== null ? (
          <Link
            to="/profile"
            className={cn(GPT_HEADER_ROW, "gap-3 no-underline")}
          >
            <ProfilePicture user={currentUser} height={38} badge={null} />
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="m-0 truncate font-dm-sans text-ui-body font-semibold text-ds-slate-700">
                {currentUser.fullname}
              </p>
              {currentUser.mainSpecialtyModel !== null ? (
                <p className="m-0 truncate font-dm-sans text-ui-note font-medium text-ds-slate-500">
                  {getItemTranslation(currentUser.mainSpecialtyModel)}
                </p>
              ) : null}
            </div>
          </Link>
        ) : null}

        <hr className="m-0 h-px w-full border-none bg-ds-slate-100" />

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pt-3 pb-2">
          <p className="m-0 font-dm-sans text-ui-note font-bold text-ds-slate-600 uppercase">
            {t("Recents")}
          </p>

          {recents.length > 0 ? (
            <div className="flex flex-col gap-1 pt-2">
              {recents.map((recent) => (
                <Link
                  key={recent.id}
                  to={{
                    pathname: gptConversationPageLink(recent.id),
                    // Same hand-off as the general drawer: the conversation
                    // plays this drawer closed on arrival.
                    state: { historyArrival: "dismissing" },
                  }}
                  className="flex items-center gap-1 rounded-lg py-2 no-underline"
                >
                  <IconMessageCircle
                    className="size-4 shrink-0 text-ds-black"
                    stroke={1.25}
                  />
                  <span className="truncate font-dm-sans text-ui-body font-medium text-ds-black">
                    {recent.label}
                  </span>
                </Link>
              ))}
              {/* Sentinel: on screen → load the next page of each feed (fires on
                  scroll AND on mount when the list doesn't overflow). */}
              <div ref={loadMoreRef} aria-hidden className="h-px w-full" />
            </div>
          ) : anyPending && !hasError ? (
            <div className="flex flex-col gap-1 pt-2">
              {RECENTS_SKELETON_WIDTHS.map((width, index) => (
                <div key={index} className="flex items-center gap-1 py-2">
                  <Skeleton $variant="rounded" width={16} height={16} />
                  <Skeleton $variant="rounded" width={width} height={12} />
                </div>
              ))}
            </div>
          ) : !hasError && bothSucceeded ? (
            <p className="mt-4 mb-0 font-dm-sans text-ui-note font-medium text-ds-slate-500">
              {t("No conversations yet.")}
            </p>
          ) : null}

          {/* Errors are reported regardless of whether the other feed returned
              rows — never let a failure masquerade as an empty history. */}
          {hasError ? (
            <div className="mt-4 flex flex-col items-start gap-2">
              <p className="m-0 font-dm-sans text-ui-note font-medium text-ds-slate-500">
                {t("Some conversations couldn't load.")}
              </p>
              <button
                type="button"
                onClick={retry}
                className="font-dm-sans text-ui-note font-semibold text-ds-mint-800 underline"
              >
                {t("Retry")}
              </button>
            </div>
          ) : null}
        </div>

        <div className="px-5 pt-2 pb-8">
          <Button
            variant="primary"
            size="md"
            type="button"
            className="w-full"
            leftIcon={
              <IconSquareRoundedPlus className="size-4" stroke={1.25} />
            }
            onClick={onNewConversation}
          >
            {t("New conversation")}
          </Button>
        </div>
      </div>
    </>
  );
}
